import { useState, useMemo, useRef, useCallback } from "react";
import type { Payment } from "types/payment";

type Period = "3d" | "7d" | "30d" | "90d" | "12m";

const PERIOD_LABELS: Record<Period, string> = {
  "3d": "3 jours",
  "7d": "7 jours",
  "30d": "30 jours",
  "90d": "90 jours",
  "12m": "12 mois",
};

function groupPayments(payments: Payment[], period: Period) {
  const now = new Date();
  const paid = payments.filter((p) => p.state === "paid" && p.paid_at);

  if (period === "12m") {
    // Group by month — last 12 months
    const buckets: { label: string; tooltipDate: string; total: number; count: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", "");
      const tooltipDate = d.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
      buckets.push({ label, tooltipDate, total: 0, count: 0 });
      for (const p of paid) {
        const pDate = new Date(p.paid_at!);
        if (pDate.getFullYear() === d.getFullYear() && pDate.getMonth() === d.getMonth()) {
          buckets[buckets.length - 1].total += p.amount;
          buckets[buckets.length - 1].count += 1;
        }
      }
    }
    return buckets;
  }

  // Group by day
  const days = period === "3d" ? 3 : period === "7d" ? 7 : period === "30d" ? 30 : 90;
  const buckets: { label: string; tooltipDate: string; total: number; count: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    // Show label for first, last, and every ~nth point
    const showLabel = days <= 7 || i === days - 1 || i === 0 || i % Math.ceil(days / 6) === 0;
    const label = showLabel ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "") : "";
    const tooltipDate = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "");
    buckets.push({ label, tooltipDate, total: 0, count: 0 });
    for (const p of paid) {
      if (p.paid_at?.slice(0, 10) === key) {
        buckets[buckets.length - 1].total += p.amount;
        buckets[buckets.length - 1].count += 1;
      }
    }
  }
  return buckets;
}

export function PaymentsChart({
  payments,
  showPeriodSelector = false,
  defaultPeriod = "3d",
  onViewAll,
}: {
  payments: Payment[];
  showPeriodSelector?: boolean;
  defaultPeriod?: Period;
  onViewAll?: () => void;
}) {
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const buckets = useMemo(() => groupPayments(payments, period), [payments, period]);

  const totalPaid = buckets.reduce((s, b) => s + b.total, 0);
  const totalCount = buckets.reduce((s, b) => s + b.count, 0);
  const values = buckets.map((b) => b.total);
  const max = Math.max(...values, 1);

  const chartW = 580;
  const chartH = 180;

  const points = values.map((v, i) => ({
    x: values.length > 1 ? (i / (values.length - 1)) * chartW : chartW / 2,
    y: chartH - (v / max) * (chartH - 20),
  }));

  // Smooth curve
  const pathD = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  const fillD = points.length > 0
    ? `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`
    : "";

  // Y-axis labels
  const ySteps = 5;
  const yLabels = Array.from({ length: ySteps + 1 }, (_, i) => Math.round((max / ySteps) * i));

  // Labeled x positions (show up to ~8 labels)
  const labeledBuckets = buckets.map((b, i) => ({ ...b, i })).filter((b) => b.label);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * (chartW + 40);
    let nearest = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - svgX);
      if (d < minDist) { minDist = d; nearest = i; }
    });
    setHoveredIdx(nearest);
  }, [points, chartW]);

  const handleMouseLeave = useCallback(() => setHoveredIdx(null), []);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col">
          <div className="font-semibold text-black">Paiements reçus</div>
          <div className="text-hx font-semibold text-indigo-500">
            {totalPaid.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
        </div>
        <div className="flex flex-row items-center gap-3">
          {onViewAll && (
            <button onClick={onViewAll} className="text-h3 text-neutral-400 hover:text-neutral-600">
              Voir tout
            </button>
          )}
          {showPeriodSelector && (
            <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className="text-h4 font-medium px-2.5 py-1 rounded-md transition-colors"
                  style={{
                    background: period === p ? "#FFFFFF" : "transparent",
                    color: period === p ? "#111827" : "#9CA3AF",
                    boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {totalCount === 0 ? (
        <div className="flex items-center justify-center h-[180px] text-h3 text-neutral-300">
          Aucun paiement sur cette période
        </div>
      ) : (
        <div className="w-full pt-[50px]">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${chartW + 40} ${chartH + 30}`}
            width="100%"
            className="overflow-visible"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "crosshair" }}
          >
            <defs>
              <linearGradient id="payChartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {/* Grid lines */}
            {yLabels.map((label) => {
              const y = chartH - (label / max) * (chartH - 20);
              const display = label >= 1000 ? `${(label / 1000).toFixed(0)}k` : `${label}`;
              return (
                <g key={label}>
                  <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                  <text x={chartW + 8} y={y + 4} fontSize={11} fill="#a3a3a3">{display}</text>
                </g>
              );
            })}
            {/* Fill */}
            {fillD && <path d={fillD} fill="url(#payChartFill)" />}
            {/* Line */}
            {pathD && <path d={pathD} fill="none" stroke="#6366F1" strokeWidth={2.5} />}
            {/* Hover tooltip */}
            {hoveredIdx !== null && points[hoveredIdx] && (
              <>
                <line
                  x1={points[hoveredIdx].x} y1={0}
                  x2={points[hoveredIdx].x} y2={chartH}
                  stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4 2"
                />
                <circle cx={points[hoveredIdx].x} cy={points[hoveredIdx].y} r={5} fill="#6366F1" stroke="white" strokeWidth={2} />
                <g transform={`translate(${Math.min(Math.max(points[hoveredIdx].x - 55, 0), chartW - 110)}, ${points[hoveredIdx].y < 60 ? points[hoveredIdx].y + 15 : points[hoveredIdx].y - 50})`}>
                  <rect width={110} height={40} rx={8} fill="#1a1a1a" />
                  <text x={55} y={16} textAnchor="middle" fontSize={11} fill="#c8c8c8">
                    {buckets[hoveredIdx].tooltipDate}
                  </text>
                  <text x={55} y={32} textAnchor="middle" fontSize={13} fill="white" fontWeight="600">
                    {buckets[hoveredIdx].total.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </text>
                </g>
              </>
            )}
            {/* X labels */}
            {labeledBuckets.map((b) => (
              <text
                key={b.i}
                x={values.length > 1 ? (b.i / (values.length - 1)) * chartW : chartW / 2}
                y={chartH + 20}
                textAnchor="middle"
                fontSize={11}
                fill="#a3a3a3"
              >
                {b.label}
              </text>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

export function PaymentSuccessRateChart({ payments }: { payments: Payment[] }) {
  const stats = useMemo(() => {
    // Only count payments that have reached a terminal state
    const terminal = payments.filter((p) => ["paid", "error", "cancelled"].includes(p.state));
    const paid = terminal.filter((p) => p.state === "paid").length;
    const failed = terminal.filter((p) => p.state === "error").length;
    const cancelled = terminal.filter((p) => p.state === "cancelled").length;
    const total = terminal.length;
    const rate = total > 0 ? Math.round((paid / total) * 100) : 0;
    return { paid, failed, cancelled, total, rate };
  }, [payments]);

  // Donut chart
  const size = 120;
  const strokeW = 14;
  const r = (size - strokeW) / 2;
  const circumference = 2 * Math.PI * r;
  const successArc = stats.total > 0 ? (stats.paid / stats.total) * circumference : 0;
  const failedArc = stats.total > 0 ? (stats.failed / stats.total) * circumference : 0;
  const cancelledArc = stats.total > 0 ? (stats.cancelled / stats.total) * circumference : 0;

  const rateColor = "#6366F1";

  return (
    <div className="flex flex-col gap-3">
      <div className="font-semibold text-black">Taux de réussite</div>
      {stats.total === 0 ? (
        <div className="flex items-center justify-center h-[120px] text-h3 text-neutral-300">
          Aucun paiement terminé
        </div>
      ) : (
        <div className="flex flex-row items-center gap-6">
          {/* Donut */}
          <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="overflow-visible" style={{ transform: "rotate(-90deg)" }}>
              {/* Background */}
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F5F5F5" strokeWidth={strokeW} />
              {/* Success — solid indigo */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#6366F1" strokeWidth={strokeW}
                strokeDasharray={`${successArc} ${circumference}`}
                strokeDashoffset={0}
                strokeLinecap="round"
              />
              {/* Failed — light rose */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#F43F5E15" strokeWidth={strokeW}
                strokeDasharray={`${failedArc} ${circumference}`}
                strokeDashoffset={-successArc}
                strokeLinecap="round"
              />
              {/* Cancelled — light sky */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#0EA5E915" strokeWidth={strokeW}
                strokeDasharray={`${cancelledArc} ${circumference}`}
                strokeDashoffset={-(successArc + failedArc)}
                strokeLinecap="round"
              />
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-bold" style={{ color: rateColor }}>{stats.rate}%</span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#6366F1" }} />
              <span className="text-h3" style={{ color: "#6366F1" }}>Payés — {stats.paid}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#F43F5E15" }} />
              <span className="text-h3" style={{ color: "#F43F5E" }}>Erreurs — {stats.failed}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#0EA5E915" }} />
              <span className="text-h3" style={{ color: "#0EA5E9" }}>Annulés — {stats.cancelled}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
